// Shared by any page's "History" button. Given a job (and the full list of
// jobs currently loaded on that page), returns every regular note and
// supervisor note ever added to that job PLUS any job connected to it,
// combined into one chronological running record.
//
// "Connected" jobs are:
//  - The job itself (a recurring job's notes already live on one doc, so
//    nothing extra is needed there).
//  - Jobs created via "Continue" (Home.js / Calendar.js / EmbeddedCalendar.js
//    / JobHistory.js / SendInvoices.js).
//  - Jobs created via "Extend From Existing WO Stem" (Scheduling.js).
//
// Both of those creation paths give the new job a WO# equal to the
// original WO with a "-N" suffix appended (e.g. 3345 -> 3345-2 -> 3345-3,
// matching getContinuedWO in Home.js), so connected jobs can be found just
// by comparing that shared "stem" — no extra stored link field is required.

export const getJobHistoryWOStem = (wo = "") => {
  const text = String(wo || "").trim();
  if (!text) return "";

  // 3345-2 -> 3345
  const numericBranch = text.match(/^(\d+)-\d+$/);
  if (numericBranch) return numericBranch[1];

  // PJMC-10021-2 -> PJMC-10021, but PJMC-10021 stays PJMC-10021
  const prefixedBranch = text.match(/^([A-Za-z]+-\d+)-\d+$/);
  if (prefixedBranch) return prefixedBranch[1];

  return text;
};

// Finds every job connected to `job` (including job itself) within `allJobs`.
export const getConnectedJobs = (job, allJobs = []) => {
  if (!job) return [];

  const stem = getJobHistoryWOStem(job.wo);

  const connected = stem
    ? allJobs.filter((j) => getJobHistoryWOStem(j.wo) === stem)
    : [];

  if (!connected.some((j) => j.id === job.id)) {
    connected.push(job);
  }

  return connected;
};

// Combines every note + supervisor note from `job` and all jobs connected
// to it into one flat, chronologically sorted (newest first) list.
export const getJobHistoryEntries = (job, allJobs = []) => {
  if (!job) return [];

  const connectedJobs = getConnectedJobs(job, allJobs);
  const entries = [];

  connectedJobs.forEach((j) => {
    const woLabel = j.wo || j.id;

    // Regular notes on a non-recurring job live in a flat `notes` array.
    (Array.isArray(j.notes) ? j.notes : []).forEach((note) => {
      entries.push({
        type: "Note",
        wo: woLabel,
        occurrenceDate: note.occurrenceDate || "",
        text: note.text || "",
        lang: note.lang || "",
        author: note.author || "Unknown",
        date: note.timestamp || note.createdAt || "",
        files: Array.isArray(note.files) ? note.files : [],
      });
    });

    // Regular notes on a recurring job live in `occurrenceNotes`, keyed by
    // the occurrence date, each holding its own array of notes.
    if (j.occurrenceNotes && typeof j.occurrenceNotes === "object") {
      Object.entries(j.occurrenceNotes).forEach(([occurrenceDate, notesForDate]) => {
        (Array.isArray(notesForDate) ? notesForDate : []).forEach((note) => {
          entries.push({
            type: "Note",
            wo: woLabel,
            occurrenceDate,
            text: note.text || "",
            lang: note.lang || "",
            author: note.author || "Unknown",
            date: note.timestamp || note.createdAt || occurrenceDate || "",
            files: Array.isArray(note.files) ? note.files : [],
          });
        });
      });
    }

    // Supervisor notes always live in one flat array, recurring or not.
    (Array.isArray(j.supervisorNotes) ? j.supervisorNotes : []).forEach((note) => {
      entries.push({
        type: "Supervisor Note",
        wo: woLabel,
        occurrenceDate: note.occurrenceDate || "",
        text: note.text || "",
        lang: note.lang || "",
        author: note.author || "Unknown",
        authorRole: note.authorRole || "",
        date: note.createdAt || "",
      });
    });
  });

  entries.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return (Number.isNaN(dateB) ? 0 : dateB) - (Number.isNaN(dateA) ? 0 : dateA);
  });

  return entries;
};
